import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import { Autocomplete, TextField, CircularProgress, Chip } from "@mui/material";
import { getOperators } from "services/operatorService";
import sessionHelperInstance from "services/sessionHelper";
import { getOperatorFilterLabel } from "utils/operatorFilterOptions";

const OperatorMultiSelect = ({
  label = "Select Operators",
  value = [],
  onChange,
  error,
  helperText,
  disabled = false,
  fullWidth = true,
  size = "medium" as "small" | "medium",
  populatedOperators = {}, // Object with operatorId as key and operator data as value
}) => {
  const [open, setOpen] = useState(false);
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [inputValue, setInputValue] = useState("");
  const [pageNo, setPageNo] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const pageSize = 20;

  // Convert value array of IDs to array of operator objects
  const selectedOperators = value.map((id) => {
    // First check if operator is in options
    const foundInOptions = options.find((op) => op.id === id);
    if (foundInOptions) return foundInOptions;

    // Otherwise check populatedOperators
    const operator = populatedOperators[id];
    if (operator) {
      return {
        id: operator._id,
        label: operator.externalOpId
          ? `${operator.operatorName} (${operator.externalOpId})`
          : operator.operatorName,
        ...operator,
      };
    }

    // Fallback to just ID
    return { id, label: id };
  });

  // Initialize operators from populatedOperators on mount
  useEffect(() => {
    if (populatedOperators && Object.keys(populatedOperators).length > 0) {
      const initialOperators = Object.values(populatedOperators).map(
        (op: any) => ({
          id: op._id,
          label: op.externalOpId
            ? `${op.operatorName} (${op.externalOpId})`
            : op.operatorName,
          ...op,
        }),
      );
      setOptions(initialOperators);
    }
  }, [populatedOperators]);

  // Fetch operators with pagination and search
  useEffect(() => {
    let active = true;

    if (!open) {
      return undefined;
    }

    (async () => {
      setLoading(true);
      try {
        const response = await getOperators({
          pageNo: 1,
          pageSize,
          search: inputValue || "",
          sortBy: "name",
          sortDirection: 1,
          liveStatus:
            sessionHelperInstance.operatorFilterPreference?.liveStatus,
          activeStatus:
            sessionHelperInstance.operatorFilterPreference?.activeStatus,
        });

        if (active && response?.data?.operators) {
          const newOperators = response.data.operators.map((op) => ({
            id: op._id,
            label: op.externalOpId
              ? `${op.operatorName} (${op.externalOpId})`
              : op.operatorName,
            ...op,
          }));

          // Ensure selected operators are always in the options
          const selectedOps = selectedOperators.filter(
            (op) => op.label !== op.id,
          );
          const selectedIds = new Set(selectedOps.map((op) => op.id));
          const fetchedIds = new Set(newOperators.map((op) => op.id));

          // Add selected operators that are not in the fetched results
          const missingSelected = selectedOps.filter(
            (op) => !fetchedIds.has(op.id),
          );
          const combinedOperators = [...missingSelected, ...newOperators];

          setOptions(combinedOperators);
          setHasMore(newOperators.length === pageSize);
          setPageNo(1);
        }
      } catch (error) {
        console.error("Failed to fetch operators:", error);
      } finally {
        if (active) {
          setLoading(false);
        }
      }
    })();

    return () => {
      active = false;
    };
  }, [open, inputValue]);

  // Load more operators on scroll
  const loadMoreOperators = async () => {
    if (loading || !hasMore) return;

    setLoading(true);
    try {
      const nextPage = pageNo + 1;
      const response = await getOperators({
        pageNo: nextPage,
        pageSize,
        search: inputValue || "",
        sortBy: "name",
        sortDirection: 1,
        liveStatus: sessionHelperInstance.operatorFilterPreference?.liveStatus,
        activeStatus:
          sessionHelperInstance.operatorFilterPreference?.activeStatus,
      });

      if (response?.data?.operators) {
        const newOperators = response.data.operators.map((op) => ({
          id: op._id,
          label: op.externalOpId
            ? `${op.operatorName} (${op.externalOpId})`
            : op.operatorName,
          ...op,
        }));

        setOptions((prev) => {
          // Remove duplicates by id
          const uniqueIds = new Set(prev.map((op) => op.id));
          const filtered = newOperators.filter((op) => !uniqueIds.has(op.id));
          return [...prev, ...filtered];
        });

        setHasMore(newOperators.length === pageSize);
        setPageNo(nextPage);
      }
    } catch (error) {
      console.error("Failed to fetch more operators:", error);
    } finally {
      setLoading(false);
    }
  };

  // Handle scroll for infinite loading
  const handleListboxScroll = (event: any) => {
    const listboxNode = event.currentTarget;
    const position = listboxNode.scrollTop + listboxNode.clientHeight;
    const bottom = listboxNode.scrollHeight - 10; // 10px threshold

    if (position >= bottom && hasMore && !loading) {
      loadMoreOperators();
    }
  };

  return (
    <Autocomplete
      multiple
      size={size}
      fullWidth={fullWidth}
      disabled={disabled}
      open={open}
      onOpen={() => setOpen(true)}
      onClose={() => setOpen(false)}
      value={selectedOperators}
      onChange={(_event, newValue) => {
        // Convert array of operator objects to array of IDs
        const selectedIds = newValue.map((op) => op.id);
        if (onChange) {
          onChange(selectedIds);
        }
        setInputValue("");
      }}
      inputValue={inputValue}
      onInputChange={(_event, newInputValue, reason) => {
        if (reason === "input") {
          setInputValue(newInputValue);
          setPageNo(1);
          setHasMore(true);
        }
      }}
      isOptionEqualToValue={(option, value) => option.id === value.id}
      getOptionLabel={(option) => option.label || option.id}
      options={options}
      loading={loading}
      loadingText="Loading operators..."
      noOptionsText={`No operators match the current filter (${getOperatorFilterLabel(
        sessionHelperInstance.operatorFilterPreference?.liveStatus,
        sessionHelperInstance.operatorFilterPreference?.activeStatus,
      )}). Change it on the Operators page.`}
      ListboxProps={{
        onScroll: handleListboxScroll,
        style: { maxHeight: "400px" },
      }}
      renderTags={(tagValue, getTagProps) =>
        tagValue.map((option, index) => (
          <Chip
            {...getTagProps({ index })}
            key={option.id}
            label={option.label}
            size="small"
          />
        ))
      }
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          error={Boolean(error)}
          helperText={helperText}
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {loading ? (
                  <CircularProgress color="inherit" size={20} />
                ) : null}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
    />
  );
};

OperatorMultiSelect.propTypes = {
  label: PropTypes.string,
  value: PropTypes.arrayOf(PropTypes.string),
  onChange: PropTypes.func.isRequired,
  error: PropTypes.bool,
  helperText: PropTypes.string,
  disabled: PropTypes.bool,
  fullWidth: PropTypes.bool,
  size: PropTypes.oneOf(["small", "medium"]),
  populatedOperators: PropTypes.object,
};

export default OperatorMultiSelect;
